import _ from 'lodash';
import { ApiDriver } from '@pearsontechnology/aero-plugin-api';
import { loadPluginView } from 'pluginServices/lifecycle/loadPluginView';
import SubscriptionRegister from './SubscriptionRegister';
import { resources, methods } from './constants';

const createKey = () => _.times(5, Math.random).map((x) => Math.floor(x * 100000)).join('-');

const getPluginDriverOptions = () => ({
  featureResources: [
    resources.STATE_RESOURCE,
    resources.CONTEXT_RESOURCE,
    resources.CHILD_RESOURCE,
    resources.HISTORY_RESOURCE,
    resources.ROUTE_RESOURCE,
    resources.DRAG_STATE_RESOURCE,
    resources.THEME_RESOURCE,
    resources.EDITOR_RESOURCE,
    resources.SELECTION_RESOURCE,
  ],
  context: 'REVIEW',
});

const pickEntryPoint = (entryPoints, context) => (
  entryPoints[context] || entryPoints.LEARNER || Object.values(entryPoints)[0]);

export const noopSubscribe = () => ({
  subscriptionId: createKey(),
  unsubscribe: _.noop,
});

export const noopGet = () => {};
export const noopSet = () => {};

const pretendSubscribe = (getFn) => async ({ optionalCallbackFn, gateway, ...params }) => {
  const subscriptionId = createKey();
  const oneTimeValue = await getFn(params);

  if (optionalCallbackFn) {
    optionalCallbackFn(oneTimeValue);
  } else {
    gateway.notify({ subscriptionId, payload: oneTimeValue });
  }

  return {
    subscriptionId,
    unsubscribe: _.noop,
  };
};

const mergeState = (config, studentScope) => {
  const _config = JSON.parse(config);
  const scopeData = studentScope && studentScope.data;
  if (scopeData) {
    const _studentScope = JSON.parse(scopeData);
    return _.merge(_config, _studentScope);
  }
  return _config;
};

const getState = async ({ path, state }) => (
  path ? _.get(state, path) : state);

const getSelection = async (focusedComponentId) => {
  const componentId = focusedComponentId;

  return {
    selection: [{
      type: 'REVIEW_FOCUS_SELECTION',
      path: ['**', componentId],
    }],
  };
};

const buildSubscription = (subscriptions, id, resourceName, methodName) =>
  // return the actual subscription method.
  ({ optionalCallbackFn, gateway }) => {
    const subscriptionId = createKey();

    const notifier = (payload) => {
      if (optionalCallbackFn) {
        optionalCallbackFn(payload);
      } else {
        gateway.notify({ subscriptionId, payload });
      }
    };

    // add to the subscription list so that it may be called later.
    subscriptions.addSubscription({ id, resourceName, methodName, notifier });

    return {
      subscriptionId,
      unsubscribe: _.noop,
    };
  };

/**
 * PSA: The term 'PROVIDERS' comes from the plugin lib, which used the term to refer to
 * something that provided a service to a resource across an iframe app boundary.
 */
const getProviders = ({
  apiDriver,
  gateway,
  config,
  context,
  coursewareType,
  componentConfigs,
  focusedComponentId,
  studentScopes,
  studentScope,
  id,
  subscriptions,
}) => {
  const state = mergeState(config, studentScope);

  const selection = getSelection(focusedComponentId);

  return {
    [resources.SELECTION_RESOURCE]: {
      [methods.GET]: () => selection,
      [methods.SET]: noopSet,
      [methods.SUBSCRIBE]: ({ optionalCallbackFn }) => (
        buildSubscription(subscriptions, id, resources.SELECTION_RESOURCE, methods.SUBSCRIBE)({ optionalCallbackFn, focusedComponentId, componentConfigs, gateway })),
    },
    [resources.STATE_RESOURCE]: {
      [methods.GET]: ({ path }) => getState({ state, path }),
      [methods.SET]: noopSet,
      [methods.SUBSCRIBE]: (subscriptionArgs) => pretendSubscribe(getState)({ ...subscriptionArgs, state, gateway }),
    },
    [resources.CONTEXT_RESOURCE]: {
      [methods.GET]: async () => context,
    },
    [resources.CHILD_RESOURCE]: {
      [methods.INIT_CHILD]: ({ childId }) => initChild({ apiDriver, context, coursewareType, componentConfigs, childId, studentScopes }),
    },
    [resources.HISTORY_RESOURCE]: {
      [methods.SUBSCRIBE]: noopSubscribe,
      [methods.GET]: noopGet,
      [methods.SET]: noopSet,
    },
    [resources.ROUTE_RESOURCE]: {
      [methods.SUBSCRIBE]: noopSubscribe,
      [methods.GET]: noopGet,
      [methods.SET]: noopSet,
    },
    [resources.DRAG_STATE_RESOURCE]: {
      [methods.SUBSCRIBE_TO_DRAGGABLES]: noopSubscribe,
      [methods.GET]: noopGet,
      [methods.SET]: noopSet,
    },
    [resources.THEME_RESOURCE]: {
      [methods.SUBSCRIBE_TO_THEME_CSS]: noopSubscribe,
    },
    [resources.EDITOR_RESOURCE]: {
      [methods.SUBSCRIBE_TO_SELECTION]: noopSubscribe,
    },
  };
};

// https://stackoverflow.com/questions/37949981/call-child-method-from-parent
// comeback and do this here.
//
// this should probably return a collection of subscription ids gainst resource names.
// that way we could use createref to notify.
const connectProviders = (providers, connectionResponse) => {
  // The plugin lib's fluid api for registering resource providers
  // is misleading.  In actual fact, all the methods must be registered
  // from one service before another service may be registered.
  // I.e., its not encapsulated.
  // TODO: fix this.

  // Build our providers
  const providersToRegister = Object.entries(providers(connectionResponse));

  // Register each provider;
  providersToRegister.forEach(([resourceName, providerMethods]) => {
    const registeredResource = connectionResponse.gateway.register(resourceName);
    const methodsToRegister = Object.entries(providerMethods);

    // and each method inside that provider
    methodsToRegister.forEach(([methodName, fn]) => {
      registeredResource.method(methodName, ({ data }) => fn(data));
    });
  });
};

const initChild = async ({ apiDriver, context, coursewareType, componentConfigs, childId, studentScopes, subscriptions }) => {
  try {
    const id = childId;
    const studentScope = studentScopes[childId];
    const componentConfig = componentConfigs[id];
    if (!componentConfig) {
      throw new Error(`Could not load child component: ${id}`);
    }
    const config = componentConfig.config;
    const entryPoint = pickEntryPoint(componentConfig.plugin.entryPoints, context);
    const nonce = createKey();

    apiDriver.listenForConnectionNonce({ id, nonce, context, coursewareType },
      ({ gateway }) => {
        connectProviders(getProviders, { id, subscriptions, apiDriver, gateway, config, context, studentScope });
      });

    return {
      nonce,
      entryPoint,
    };
  } catch (err) {
    throw new Error(err);
  }
};

const startPlugin = async ({ el, parentConfig, componentConfigs, studentId, studentScope, context, componentId }) => {
  try {
    const _studentScope = studentScope[studentId] || {};
    const managedIframe = await loadPluginView(pickEntryPoint(parentConfig.plugin.entryPoints, context), el);
    const apiDriver = new ApiDriver({ ...getPluginDriverOptions(), context });

    // we dont need to use a real for the screen.
    const id = createKey();

    // a register of subscriptions so that we may notify them later.
    // Using an initial id gives us a mechanism to easily identify root
    // (screen) subscriptions over the child component subscriptions.
    const subscriptions = new SubscriptionRegister(id);

    // register this root plugin.
    apiDriver.listenForConnections({ id, windowToListenTo: managedIframe.getIframe() }, ({ gateway, featureResources }) => {
      try {
        connectProviders(getProviders, {
          apiDriver,
          config: parentConfig.config,
          componentConfigs,
          focusedComponentId: componentId,
          gateway,
          context,
          studentScope: _studentScope.scope[parentConfig.id],
          studentScopes: _studentScope.scope,
          id,
          subscriptions,
        });
        return { context, featureResources };
      } catch (cbError) {
        throw new Error(cbError);
      }
    });

    managedIframe.load();

    return {
      subscriptions,
      destroy: () => {},
    };
  } catch (err) {
    throw new Error(err);
  }
};

export const initPlugin = async (...params) => {
  const pluginResponse = await startPlugin(...params);

  return pluginResponse;
};

